import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { Validators, FormGroup, AbstractControl, FormBuilder } from '@angular/forms';
import { LeaseAgreementCategory, LeasePeriodKind, LeasePeriodKindToString, TerminatedByKind } from '../../model/const/enums';
import { Contract } from '../../model/contract';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { LocalDate } from '../../../core/model/local-date';
import { PremisesContractService } from '../../service/data/premises-contract.service';
import { DwellingContractService } from '../../service/data/dwelling-contract.service';
import { ParkingContractService } from '../../service/data/parking-contract.service';
import { OtherContractService } from '../../service/data/other-contract.service';
import { BlockContractService } from '../../service/data/block-contract.service';

@Component({
    selector: 'app-notice-period',
    templateUrl: './notice-period.component.html',
    styleUrls: ['./notice-period.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class NoticePeriodComponent implements OnInit {
  private readonly wholeNumberValidator = (control: AbstractControl) => {
    if (!control?.value) {
      return null;
    }

    return Validators.pattern(/^\d+\s*$/)(control);
  };

  constructor(
    private _fc: FormControlExFactory,
    protected fb: FormBuilder,
    private premisesContractService: PremisesContractService,
    private dwellingContractService: DwellingContractService,
    private parkingContractService: ParkingContractService,
    private otherContractService: OtherContractService,
    private blockContractService: BlockContractService
  ) {}

  private _form: FormGroup;
  @Input() set form(value: AbstractControl) {
    this._form = value as FormGroup;
  }
  get form(): FormGroup {
    return this._form;
  }
  @Input() dataContext: Contract;
  @Output() fromChange: EventEmitter<LocalDate> = new EventEmitter();

  private setLocalDateFormValue(name: string, value: LocalDate, form?: FormGroup) {
    if (!form) form = this.form;
    if (!form.contains(name)) return;
    let group = form.controls[name] as FormGroup;
    if (!group.contains('year') || !group.contains('month') || !group.contains('day')) return;

    if (!value) value = new LocalDate(null, null, null);

    let currentValue = this.getLocalDateFormValue(name, form);

    if (value.year == currentValue.year && value.month == currentValue.month && value.day == currentValue.day) return;

    group.setValue({ year: value.year, month: value.month, day: value.day });

    if (this._initialized) group.markAsDirty();
  }

  private getLocalDateFormValue(name: string, form?: FormGroup): LocalDate {
    if (!form) form = this.form;
    if (!form.contains(name)) return null;
    let group = form.controls[name] as FormGroup;
    if (!group.contains('year') || !group.contains('month') || !group.contains('day')) return null;

    return new LocalDate(group.controls['year'].value, group.controls['month'].value, group.controls['day'].value);
  }

  private rememberCurrentValues() {
    if (this.prolongationTime.value) this._prolongationTime = this.prolongationTime.value;
    const currentLeasePeriod = this.dataContext.noticePeriod.leasePeriod;
    if (currentLeasePeriod === LeasePeriodKind.AutomaticExtension || currentLeasePeriod === LeasePeriodKind.LimitedPeriod) {
      this._allowEarlyTermination = this.allowEarlyTermination.value;
    }
    if (this.lastDebit.hasValue()) this._lastDebit = this.lastDebit.toDate();
    if (this.terminationDate.hasValue()) this._terminationDate = this.terminationDate.toDate();
    if (this.from.hasValue()) this._from = this.from.toDate();
    if (this.to.hasValue()) this._toDate = this.to.toDate();
    if (this.desiredMoveOutDate.hasValue()) this._desiredMoveOutDate = this.desiredMoveOutDate.toDate();
  }

  private _prolongationTime: number;
  private _allowEarlyTermination: boolean;
  private _lastDebit: Date;
  private _terminationDate: Date;
  private _desiredMoveOutDate: Date;
  private _toDate: Date;
  private _from: Date;
  private _updatingState: boolean;

  get from(): LocalDate {
    return this.getLocalDateFormValue('from');
  }
  set from(value: LocalDate) {
    this.setLocalDateFormValue('from', value);
    this.fromChanged(value);
  }
  fromChanged(value: LocalDate) {
    this.setLocalDateFormValue('fromDate', value, this.form.parent as FormGroup);
    this.dataContext.fromDate = value;
    this.fromChange.emit(value);
  }

  get to(): LocalDate {
    return this.getLocalDateFormValue('to');
  }
  set to(value: LocalDate) {
    this.setLocalDateFormValue('to', value);
    this.toChanged(value);
  }
  toChanged(value: LocalDate) {
    this.setLocalDateFormValue('toDate', value, this.form.parent as FormGroup);
    this.dataContext.toDate = value;
    this._updateNextExtensionPeriod();
  }

  get lastDebit(): LocalDate {
    return this.getLocalDateFormValue('lastDebit');
  }
  set lastDebit(value: LocalDate) {
    this.setLocalDateFormValue('lastDebit', value);
    this.lastDebitChanged(value);
  }
  lastDebitChanged(value: LocalDate) {
    this.setLocalDateFormValue('lastBillingDate', value, this.form.parent as FormGroup);
    this.dataContext.lastBillingDate = value;
  }

  get desiredMoveOutDate(): LocalDate {
    return this.getLocalDateFormValue('desiredMoveOutDate');
  }
  set desiredMoveOutDate(value: LocalDate) {
    this.setLocalDateFormValue('desiredMoveOutDate', value);
    this.desiredMoveOutDateChanged(value);
  }
  desiredMoveOutDateChanged(value: LocalDate) {
    this.setLocalDateFormValue('desiredMoveOutDate', value, this.form.parent as FormGroup);
    this.dataContext.desiredMoveOutDate = value;
  }


  get terminationDate(): LocalDate {
    return this.getLocalDateFormValue('terminationDate');
  }
  set terminationDate(value: LocalDate) {
    this.setLocalDateFormValue('terminationDate', value);
    this.terminationDateChanged(value);
  }
  terminationDateChanged(value: LocalDate) {
    this.setLocalDateFormValue('terminationDate', value, this.form.parent as FormGroup);
    this.dataContext.terminationDate = value;
  }

  get landlordDuration(): AbstractControl {
    return this.form.controls['landlordDuration'];
  }
  get leasePeriod(): AbstractControl {
    return this.form.controls['leasePeriod'];
  }
  leasePeriods: any[];

  get terminatedBy(): AbstractControl {
    return this.form.controls['terminatedBy'];
  }
  terminatedBySelection: any[];

  get prolongationTime(): FormControlEx {
    return this.form.controls['prolongationTime'] as FormControlEx;
  }
  get allowEarlyTermination(): FormControlEx {
    return this.form.controls['allowEarlyTermination'] as FormControlEx;
  }
  showLastDebitDate: boolean;
  showProlongation: boolean;
  showToDate: boolean;
  showAllowEarlyTermination: boolean;
  earlyTerminationActiveOnCompany: boolean;

  setEarlyTerminationConfiguration(isActive: boolean, defaultValue: boolean): void {
    this.earlyTerminationActiveOnCompany = isActive;
    this._allowEarlyTermination = defaultValue;

    const leasePeriod = this.leasePeriod.value;
    const isApplicableLeasePeriod =
      leasePeriod === LeasePeriodKind.AutomaticExtension || leasePeriod === LeasePeriodKind.LimitedPeriod;
    const allowEarlyTermination = isActive && isApplicableLeasePeriod ? defaultValue : false;

    this.dataContext.noticePeriod.allowEarlyTermination = allowEarlyTermination;
    this.allowEarlyTermination.setValue(allowEarlyTermination);
    this.showAllowEarlyTermination = isActive && isApplicableLeasePeriod;
  }

  get tenantDuration(): AbstractControl {
    return this.form.controls['tenantDuration'];
  }

  private applyWholeNumberValidator(control: AbstractControl): void {
    control.addValidators(this.wholeNumberValidator);
    control.updateValueAndValidity({ emitEvent: false });
  }

  private _initForm() {
    this.form.addControl(
      'lastDebit',
      this.fb.group({
        year: this._fc.create('YEAR', null),
        month: this._fc.create('MONTH', null),
        day: this._fc.create('DAY', null),
      })
    );
    this.form.addControl(
      'desiredMoveOutDate',
      this.fb.group({
        year: this._fc.create('YEAR', null),
        month: this._fc.create('MONTH', null),
        day: this._fc.create('DAY', null),
      })
    );
    this.form.addControl(
      'terminationDate',
      this.fb.group({
        year: this._fc.create('YEAR', null),
        month: this._fc.create('MONTH', null),
        day: this._fc.create('DAY', null),
      })
    );
    this.form.addControl(
      'from',
      this.fb.group({
        year: this._fc.create('YEAR', null),
        month: this._fc.create('MONTH', null),
        day: this._fc.create('DAY', null),
      })
    );
    this.form.addControl(
      'landlordDuration',
      this._fc.createWithOptions('LEASE_AGREEMENT_NOTICE_PERIOD_FORM.LANDLORD_LABEL', this.dataContext.noticePeriod.landlordDuration, {
        required: true,
        minValue: 0,
      })
    );
    this.form.addControl(
      'leasePeriod',
      this._fc.create('LEASE_AGREEMENT_NOTICE_PERIOD_FORM.LEASE_PERIOD_LABEL', this.dataContext.noticePeriod.leasePeriod)
    );
    this.form.addControl(
      'allowEarlyTermination',
      this._fc.create('LEASE_AGREEMENT_NOTICE_PERIOD_FORM.ALLOW_EARLY_TERMINATION', this.dataContext.noticePeriod.allowEarlyTermination)
    );
    this.form.addControl(
      'terminatedBy',
      this._fc.create('TERMINATE_LEASE_AGREEMENT_FORM.TERMINATED_BY_LABEL', this.dataContext.noticePeriod.terminatedBy)
    );
    this.form.addControl(
      'prolongationTime',
      this._fc.create('LEASE_AGREEMENT_NOTICE_PERIOD_FORM.PROLONGATION_TIME_LABEL', this.dataContext.noticePeriod.prolongationTime)
    );
    this.form.addControl(
      'tenantDuration',
      this._fc.createWithOptions('LEASE_AGREEMENT_NOTICE_PERIOD_FORM.TENANT_LABEL', this.dataContext.noticePeriod.tenantDuration, {
        required: true,
        minValue: 0,
      })
    );

    this.applyWholeNumberValidator(this.prolongationTime);
    this.applyWholeNumberValidator(this.tenantDuration);
    this.applyWholeNumberValidator(this.landlordDuration);
    this.form.addControl(
      'to',
      this.fb.group({
        year: this._fc.create('COMMON.YEAR', null),
        month: this._fc.create('COMMON.MONTH', null),
        day: this._fc.create('COMMON.DAY', null),
      })
    );

    // A new NoticePeriod starts with next month's first day, but selecting a vacant object puts
    // the actual vacancy date on the contract. Saved contracts instead keep their persisted value.
    const noticePeriodFrom = this.dataContext.isNew
      ? this.dataContext.fromDate
      : this.dataContext.noticePeriod?.from ?? this.dataContext.fromDate;
    this.setLocalDateFormValue('from', noticePeriodFrom);
    if (this.dataContext.isNew && !LocalDate.fromDate(noticePeriodFrom)?.hasValue()) {
      let date = LocalDate.today();
      date.addMonths(1);
      this.setLocalDateFormValue('from', LocalDate.fromDate(date.firstDayInMonth()));
    }
    this.setLocalDateFormValue('to', this.dataContext.toDate);
    this.setLocalDateFormValue('lastDebit', this.dataContext.noticePeriod.lastDebit);
    this.setLocalDateFormValue('desiredMoveOutDate', this.dataContext.noticePeriod.desiredMoveOutDate);
    this.setLocalDateFormValue('terminationDate', this.dataContext.noticePeriod.terminationDate);
  }

  private _updateNoticePeriodProlongationTime(value: number): void {
    if (value) {
      this.dataContext.noticePeriod.prolongationTime = value;
      this._updateNextExtensionPeriod();
    }
  }

  private _updateNoticePeriodLandlordDuration(value: number): void {
    if (value) {
      this.dataContext.noticePeriod.landlordDuration = value;
      this._updateNextExtensionPeriod();
    }
  }

  /**
   * Asks the API to recalculate the landlord contract period for the notice period being edited.
   * The request body is dataContext, NOT the form: prepareSave() only merges the form values into a
   * model when the contract is saved, so every field the API validates has to have been mirrored
   * onto dataContext first (see the setters above and _updateState).
   */
  private _updateNextExtensionPeriod(): void {
    // _updateState rewrites several notice-period fields in sequence and each setter calls back in
    // here, which would post the notice period half-way through the switch to the new lease period.
    if (this._updatingState) return;

    // The endpoint recalculates on the STORED agreement (ChangeLeaseAgreementService.Get requires
    // an id), and an unsaved contract only holds the handful of fields this component mirrors onto
    // it, so there is nothing to ask for until the contract has been saved at least once.
    if (this.dataContext.isNew) return;

    if (this.showProlongation) {
      switch (this.dataContext.category) {
        case LeaseAgreementCategory.Premises:
          this.premisesContractService.retrieveUpdatedLeaseAgreementPremises(this.dataContext).subscribe((val: Contract) => {
            this.SetContractPeriodValues(val);
          });
          break;
        case LeaseAgreementCategory.Parking:
          this.parkingContractService.retrieveUpdatedLeaseAgreementParking(this.dataContext).subscribe((val: Contract) => {
            this.SetContractPeriodValues(val);
          });
          break;
        case LeaseAgreementCategory.Dwelling:
          this.dwellingContractService.retrieveUpdatedLeaseAgreementDwelling(this.dataContext).subscribe((val: Contract) => {
            this.SetContractPeriodValues(val);
          });
          break;
        case LeaseAgreementCategory.Block:
          this.blockContractService.retrieveUpdatedLeaseAgreementBlock(this.dataContext).subscribe((val: Contract) => {
            this.SetContractPeriodValues(val);
          });
          break;
        case LeaseAgreementCategory.Other:
          this.otherContractService.retrieveUpdatedLeaseAgreementOther(this.dataContext).subscribe((val: Contract) => {
            this.SetContractPeriodValues(val);
          });
          break;
        default:
          break;
      }
    }
  }

  private SetContractPeriodValues(val: Contract) {
    this.dataContext.validContractPeriodFromLandlord = val.validContractPeriodFromLandlord;
    this.dataContext.validContractPeriodToLandlord = val.validContractPeriodToLandlord;
    this.dataContext.latestTerminationDateLandlord = val.latestTerminationDateLandlord;
  }

  private _updateState(value: LeasePeriodKind): void {
    this.rememberCurrentValues();

    // Mirror the selected lease period onto dataContext the same way the dates and durations below
    // are mirrored. dataContext is what gets posted for the recalculation, and a lease period left
    // at its stored value combined with a To date the user just entered is an inconsistent notice
    // period that the API rejects (e.g. "Invalid To date for 'Until further notice' lease type.").
    this.dataContext.noticePeriod.leasePeriod = value;

    this._updatingState = true;
    try {
      this._applyLeasePeriodState(value);
    } finally {
      this._updatingState = false;
    }

    this.showProlongation = value === LeasePeriodKind.AutomaticExtension;
    this.showLastDebitDate = value === LeasePeriodKind.LimitedPeriod || this.dataContext.hasEndDate;
    this.showToDate = value === LeasePeriodKind.AutomaticExtension;
    this.showAllowEarlyTermination = (value === LeasePeriodKind.AutomaticExtension || value === LeasePeriodKind.LimitedPeriod)
    && this.earlyTerminationActiveOnCompany ;
    this._updateNextExtensionPeriod();
  }

  private _applyLeasePeriodState(value: LeasePeriodKind): void {
    this.prolongationTime.setValidators(this.wholeNumberValidator);
    this.prolongationTime.required = false;
    switch (value) {
      case LeasePeriodKind.AutomaticExtension:
        if (!this.dataContext.hasEndDate) {
          this.lastDebit = LocalDate.fromDate(null);
        }
        this.prolongationTime.setValue(this._prolongationTime);
        this.prolongationTime.setValidators([this.wholeNumberValidator, Validators.min(1)]);
        this.prolongationTime.required = true;
        this.prolongationTime.updateValueAndValidity();
        this.allowEarlyTermination.setValue(this._allowEarlyTermination);
        break;

      case LeasePeriodKind.LimitedPeriod:
        // reset(0) not reset(): the backend requires exactly 0 for this lease period (see
        // NoticePeriodFormModel.Validate) and reset() with no argument resets to null, which
        // System.Text.Json can't bind to the non-nullable byte ProlongationTime -> 400 on save.
        this.prolongationTime.reset(0);
        this.to = LocalDate.fromDate(null);
        this.lastDebit = LocalDate.fromDate(this._lastDebit);
        this.allowEarlyTermination.setValue(this._allowEarlyTermination);
        break;

      case LeasePeriodKind.UntilFurtherNotice:
        if (!this.dataContext.hasEndDate) {
          this.lastDebit = LocalDate.fromDate(null);
        }
        this.to = LocalDate.fromDate(null);
        // See LimitedPeriod above: must reset to 0, not null.
        this.prolongationTime.reset(0);
        this.allowEarlyTermination.setValue(false);
        break;
      default:
        throw new Error('Lease period is not supported.');
    }
  }

  private _initialized: boolean;
  ngOnInit() {
    this._initForm();
    this.leasePeriods = [
      { name: LeasePeriodKindToString(LeasePeriodKind.UntilFurtherNotice), value: LeasePeriodKind.UntilFurtherNotice },
      { name: LeasePeriodKindToString(LeasePeriodKind.AutomaticExtension), value: LeasePeriodKind.AutomaticExtension },
      { name: LeasePeriodKindToString(LeasePeriodKind.LimitedPeriod), value: LeasePeriodKind.LimitedPeriod },
    ];

    this.terminatedBySelection = [
      { name: 'TERMINATE_LEASE_AGREEMENT_FORM.TENANT_TEXT', value: TerminatedByKind.Tenant },
      { name: 'TERMINATE_LEASE_AGREEMENT_FORM.LANDLORD_TEXT', value: TerminatedByKind.Landlord },
    ];
    this.earlyTerminationActiveOnCompany = this.dataContext.earlyTerminationIsUsed;
    this._allowEarlyTermination = this.dataContext.noticePeriod.allowEarlyTermination;

    this._updateState(this.leasePeriod.value);
    this.leasePeriod.valueChanges.subscribe(value => this._updateState(value));
    this.prolongationTime.valueChanges.subscribe(value => {
      this._updateNoticePeriodProlongationTime(value);
    });
    this.landlordDuration.valueChanges.subscribe(value => {
      this._updateNoticePeriodLandlordDuration(value);
    });

    this._initialized = true;
  }
}
